import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, Loader2, Building2, CheckCircle2, AlertCircle, Image as ImageIcon, Pencil, X, Plus, FlaskConical, ChevronRight } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import {
  getDiagnosticChainDetail, editDiagnosticChain, uploadChainLogo, formatDateTime,
  getDiagnosticLabs,
  validateEstablishedYear, validateGstin, validatePlace,
} from "../services/diagnosticLabsService";
import { StatusBadge, PincodeAutoAddress, getCurrentRole } from "../components/diagnostic/primitives";
import { useBlurFieldErrors } from "../components/diagnostic/useBlurFieldErrors";
import AddMemberLabPanel from "../components/diagnostic/AddMemberLabPanel";

const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-[#DCEAEA] shadow-[0_1px_2px_rgba(0,166,166,0.05)] hover:shadow-[0_8px_24px_-10px_rgba(0,166,166,0.18)] transition-shadow duration-300 overflow-hidden">
    <div className="px-6 py-4 border-b border-[#E9F2F2] bg-gradient-to-r from-[#F5FAFA] to-transparent">
      <h3 className="text-sm font-semibold text-[#0F4C81] flex items-center gap-2 before:content-[''] before:w-1 before:h-4 before:rounded-full before:bg-gradient-to-b before:from-[#00A6A6] before:to-[#2EBD85]">{title}</h3>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5 pl-3">{subtitle}</p>}
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full px-3 py-2 border border-[#CFE0E0] rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#00A6A6]/60 focus:border-[#00A6A6] transition-colors bg-[#FAFDFD] disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-700 disabled:cursor-default";

const DiagnosticChainDetail = () => {
  const navigate = useNavigate();
  const { chainId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const currentRole = getCurrentRole();
  const canAccess = currentRole === "Ops" || currentRole === "SuperAdmin";

  const [chain, setChain] = useState(null);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [addingLab, setAddingLab] = useState(false);
  const blur = useBlurFieldErrors();

  const ro = !editing;
  const memberLabs = chain?.member_labs || [];
  const logoPreview = logoFile ? URL.createObjectURL(logoFile) : chain?.logo || null;

  const toForm = (data) => ({
    brand_name: data.brand_name || "",
    legal_name: data.legal_name || "",
    description: data.description || "",
    established_year: data.established_year || "",
    hq_address_line: data.hq_address_line || "",
    hq_landmark: data.hq_landmark || "",
    hq_locality: data.hq_locality || "",
    hq_city: data.hq_city || "",
    hq_state: data.hq_state || "",
    hq_pincode: data.hq_pincode || "",
    gstin: data.gstin || "",
    active: data.active !== false,
  });

  const hydrate = (data) => {
    setChain(data);
    setForm(toForm(data));
  };

  // Member labs are the labs whose `chain` points here — query them directly so
  // the list is authoritative (the chain detail's embedded member_labs can lag).
  const loadMembers = async () => {
    try {
      // Active members only — same rule as the chain detail's embedded member_labs.
      const resp = await getDiagnosticLabs(currentRole, { chain: chainId, status: "active", items: 100 });
      const items = resp?.msg?.items || [];
      setChain((c) => (c ? { ...c, member_labs: items } : c));
    } catch { /* keep whatever the detail returned */ }
  };

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await getDiagnosticChainDetail(currentRole, chainId);
        hydrate(resp?.msg || resp);
        loadMembers();
      } catch (err) {
        setError(err.message || "Failed to load chain.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess, chainId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const descLen = form?.description?.length || 0;

  const brandErr = !form?.brand_name?.trim() ? "Brand name is required." : "";
  const yearErr = validateEstablishedYear(form?.established_year || "");
  const gstinErr = validateGstin(form?.gstin || "");
  const cityErr = validatePlace(form?.hq_city || "", "City");
  const stateErr = validatePlace(form?.hq_state || "", "State");
  const descErr = descLen > 500 ? "Description must be 500 characters or fewer." : "";

  const chainFieldKeys = ["brand_name", "established_year", "gstin", "description", "hq_city", "hq_state"];

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3200);
  };

  const cancelEdit = () => {
    setForm(toForm(chain));
    setEditing(false);
    setAddingLab(false);
    blur.reset();
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return showToast("error", "Logo must be 2MB or smaller.");
    setLogoFile(file);
    setUploadingLogo(true);
    try {
      const resp = await uploadChainLogo(currentRole, chainId, file);
      const logo = (resp?.msg || resp)?.logo;
      setChain((c) => ({ ...c, logo }));
      setLogoFile(null);
      showToast("success", "Logo uploaded.");
    } catch (err) {
      setLogoFile(null);
      showToast("error", err.message || "Logo upload failed.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    blur.touchMany(chainFieldKeys);
    const fieldErr = brandErr || descErr || yearErr || gstinErr || cityErr || stateErr;
    if (fieldErr) return showToast("error", fieldErr);

    setSaving(true);
    try {
      const payload = {
        brand_name: form.brand_name.trim(),
        legal_name: form.legal_name.trim(),
        description: form.description.trim(),
        established_year: form.established_year?.trim() || null,
        hq_address_line: form.hq_address_line.trim(),
        hq_landmark: form.hq_landmark.trim(),
        hq_locality: form.hq_locality.trim(),
        hq_city: form.hq_city.trim(),
        hq_state: form.hq_state.trim(),
        hq_pincode: form.hq_pincode.trim(),
        gstin: form.gstin.trim(),
        active: form.active,
      };
      const resp = await editDiagnosticChain(currentRole, chainId, payload);
      hydrate(resp?.msg || resp);
      setEditing(false);
      setAddingLab(false);
      blur.reset();
      showToast("success", "Chain saved successfully.");
    } catch (err) {
      showToast("error", err.message || "Failed to save chain.");
    } finally {
      setSaving(false);
    }
  };

  // A lab was attached from the picker: refresh the member list, keep the picker open.
  const onLabAttached = (lab) => {
    loadMembers();
    showToast("success", `${lab.brand_name || lab.business_name || "Lab"} added to this chain.`);
  };

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64 text-center">
        <div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F1F8F8] to-[#E5F1F1] p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/subscriber-wallet/labs")}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 truncate">
                  {loading ? "Loading…" : chain?.brand_name || "Chain"}
                </h1>
                {chain && <StatusBadge status={chain.active === false ? "inactive" : "active"} />}
              </div>
              <p className="text-sm text-gray-400">Diagnostic chain details</p>
            </div>
          </div>
          {!loading && form && (
            editing ? (
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={cancelEdit} disabled={saving}
                  className="px-4 py-2.5 text-sm text-gray-700 border border-gray-300 rounded-xl hover:bg-white disabled:opacity-50 flex items-center gap-2 transition-all">
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2.5 bg-[#0F4C81] text-white rounded-xl text-sm font-medium hover:bg-[#0C3E6B] shadow-sm disabled:opacity-50 flex items-center gap-2 transition-all">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            ) : (
              <button onClick={() => { setEditing(true); blur.reset(); }}
                className="px-4 py-2.5 bg-[#0F4C81] text-white rounded-xl text-sm font-medium hover:bg-[#0C3E6B] shadow-sm flex items-center gap-2 transition-all shrink-0">
                <Pencil className="w-4 h-4" /> Edit
              </button>
            )
          )}
        </div>

        {error && (
          <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700">{error}</div>
        )}

        {loading || !form ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-16 flex flex-col items-center gap-3 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Loading chain…</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-6">
              <SectionCard title="Brand identity">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Brand name" required>
                    <input
                      className={blur.errCls("brand_name", brandErr, inputCls)}
                      disabled={ro}
                      value={form.brand_name}
                      onChange={set("brand_name")}
                      onBlur={() => blur.touch("brand_name")}
                    />
                    {blur.msg("brand_name", brandErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("brand_name", brandErr)}</p>}
                  </Field>
                  <Field label="Legal entity name">
                    <input className={inputCls} disabled={ro} value={form.legal_name} onChange={set("legal_name")} />
                  </Field>
                  <Field label="Year established">
                    <input type="text" inputMode="numeric" maxLength={4}
                      className={blur.errCls("established_year", yearErr, inputCls)}
                      disabled={ro}
                      value={form.established_year}
                      onChange={(e) => setForm((f) => ({ ...f, established_year: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                      onBlur={() => blur.touch("established_year")}
                    />
                    {blur.msg("established_year", yearErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("established_year", yearErr)}</p>}
                  </Field>
                  <Field label="GSTIN">
                    <input
                      className={blur.errCls("gstin", gstinErr, `${inputCls} uppercase`)}
                      disabled={ro}
                      value={form.gstin}
                      maxLength={15}
                      onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 15) }))}
                      onBlur={() => blur.touch("gstin")}
                    />
                    {blur.msg("gstin", gstinErr) && <p className="mt-1 text-xs text-red-500">{blur.msg("gstin", gstinErr)}</p>}
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Description">
                      <textarea
                        rows={3}
                        className={blur.errCls("description", descErr, `${inputCls} resize-none`)}
                        disabled={ro}
                        value={form.description}
                        onChange={set("description")}
                        onBlur={() => blur.touch("description")}
                        maxLength={500}
                        placeholder={editing ? "About the chain (max 500 characters)" : ""}
                      />
                      {editing && (
                        <p className={`mt-1 text-xs ${blur.msg("description", descErr) ? "text-red-500" : "text-gray-400"}`}>
                          {blur.msg("description", descErr) || `${descLen}/500`}
                        </p>
                      )}
                    </Field>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Headquarter" subtitle="Head-office address · enter pincode to autofill locality, city & state">
                <div className="space-y-4">
                  <Field label="Address line">
                    <input className={inputCls} disabled={ro} value={form.hq_address_line} onChange={set("hq_address_line")} />
                  </Field>
                  <Field label="Landmark">
                    <input className={inputCls} disabled={ro} value={form.hq_landmark} onChange={set("hq_landmark")} />
                  </Field>
                  <PincodeAutoAddress
                    disabled={ro}
                    blur={editing ? blur : null}
                    fieldKeys={{ pincode: "hq_pincode", locality: "hq_locality", city: "hq_city", state: "hq_state" }}
                    pincode={form.hq_pincode}
                    locality={form.hq_locality}
                    city={form.hq_city}
                    state={form.hq_state}
                    onChange={(patch) => setForm((f) => ({
                      ...f,
                      ...(patch.pincode !== undefined ? { hq_pincode: patch.pincode } : {}),
                      ...(patch.locality !== undefined ? { hq_locality: patch.locality } : {}),
                      ...(patch.city !== undefined ? { hq_city: patch.city } : {}),
                      ...(patch.state !== undefined ? { hq_state: patch.state } : {}),
                    }))}
                  />
                </div>
              </SectionCard>

              <SectionCard title="Member labs">
                {/* Attach existing labs to this chain — only in edit mode */}
                {editing && (
                <div className="mb-4">
                  {!addingLab ? (
                    <button
                      type="button"
                      onClick={() => setAddingLab(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Plus className="w-4 h-4" /> Add member lab
                    </button>
                  ) : (
                    <AddMemberLabPanel
                      role={currentRole}
                      chainId={chainId}
                      chainName={chain?.brand_name}
                      onAttached={onLabAttached}
                      onClose={() => setAddingLab(false)}
                    />
                  )}
                </div>
                )}

                {memberLabs.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-gray-300" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium">No member labs yet</p>
                    <p className="text-xs text-gray-400 max-w-sm">Use “Add member lab” above, or labs onboarded under this chain will appear here.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {memberLabs.map((lab) => (
                      <li
                        key={lab.id}
                        onClick={() => navigate(`/subscriber-wallet/labs/labs/${lab.id}`)}
                        className="py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-lg"
                      >
                        <span className="text-sm text-gray-800">{lab.brand_name || lab.business_name}</span>
                        <span className="text-xs text-gray-400">{lab.city || ""}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </SectionCard>
            </div>

            {/* Right column — logo + active + meta */}
            <div className="space-y-6">
              {/* Entry to the Chain Test Lists workspace (new feature; lives on its own page). */}
              <button
                type="button"
                onClick={() => navigate(`/subscriber-wallet/labs/chains/${chainId}/test-lists`)}
                className="w-full text-left bg-white rounded-2xl border border-[#DCEAEA] shadow-[0_1px_2px_rgba(0,166,166,0.05)] hover:shadow-[0_8px_24px_-10px_rgba(0,166,166,0.18)] hover:border-[#00A6A6]/40 transition-all px-5 py-4 flex items-center gap-3"
              >
                <span className="w-10 h-10 rounded-xl bg-[#E6F6F6] flex items-center justify-center shrink-0">
                  <FlaskConical className="w-5 h-5 text-[#00A6A6]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-[#0F4C81]">Test lists</span>
                  <span className="block text-xs text-gray-400">Upload chain test lists and choose which member labs use them</span>
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
              </button>
              <SectionCard title="Logo">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-28 h-28 rounded-2xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Chain logo" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="w-7 h-7 text-gray-300" />
                    )}
                  </div>
                  {editing ? (
                    <label className={`cursor-pointer text-sm font-medium ${uploadingLogo ? "text-gray-400" : "text-blue-600 hover:text-blue-700"}`}>
                      {uploadingLogo ? "Uploading…" : logoPreview ? "Change logo" : "Upload logo"}
                      <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={uploadingLogo} onChange={handleLogoSelect} />
                    </label>
                  ) : (
                    !logoPreview && <span className="text-xs text-gray-400">No logo</span>
                  )}
                </div>
              </SectionCard>

              <SectionCard title="Active">
                <label className={`flex items-center gap-3 ${ro ? "" : "cursor-pointer"}`}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.active}
                    disabled={ro}
                    onClick={() => !ro && setForm((f) => ({ ...f, active: !f.active }))}
                    className="relative inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full disabled:cursor-default"
                  >
                    <span className={`block w-10 h-5 rounded-full transition-colors ${form.active ? "bg-emerald-500" : "bg-gray-300"}`} />
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.active ? "translate-x-5" : "translate-x-0"}`} />
                  </button>
                  <span className="text-sm text-gray-700">{form.active ? "Active" : "Inactive"}</span>
                </label>
                <p className="text-xs text-gray-400 pt-3">
                  Deactivating a chain does not deactivate its member labs — they become standalone. Remove labs from the chain's test lists first.
                </p>
              </SectionCard>

              <SectionCard title="Record">
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-400">Chain ID</dt>
                    <dd className="text-gray-600 font-mono text-xs truncate max-w-[140px]">{chain?.id}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-400">Created</dt>
                    <dd className="text-gray-600">{formatDateTime(chain?.created_at)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-400">Updated</dt>
                    <dd className="text-gray-600">{formatDateTime(chain?.updated_at)}</dd>
                  </div>
                </dl>
              </SectionCard>
            </div>
          </div>
        )}
      </div>

      {toast && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
          }`}
        >
          {toast.type === "success" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.message}
        </motion.div>
      )}
    </div>
  );
};

export default DiagnosticChainDetail;
